import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';
import { ArrowRight, Shield, Headphones, Truck, Server, ChevronRight, ChevronLeft, Mail, Phone, MapPin, Heart, Briefcase, Building2, Factory, GraduationCap, Droplets, Award, Lightbulb, Users, Code } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.png';

const SLIDE_IMAGES = [
  '/slides/slide-1.png',
  '/slides/slide-2.png',
  '/slides/slide-3.png',
  '/slides/slide-4.png',
];

const partners = ['Microsoft', 'Adobe', '3CX', 'Kaspersky', 'Ekahau'];

export default function HomePage() {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const nextSlide = useCallback(() => setCurrentSlide(p => (p + 1) % SLIDE_IMAGES.length), []);
  const prevSlide = useCallback(() => setCurrentSlide(p => (p - 1 + SLIDE_IMAGES.length) % SLIDE_IMAGES.length), []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${API}/api/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setContactSuccess(false), 4000);
    } catch {} finally { setContactLoading(false); }
  };

  const slideContent = [
    { title: t('hero.slide1.title'), subtitle: t('hero.slide1.subtitle'), brand: 'MICROSOFT' },
    { title: t('hero.slide2.title'), subtitle: t('hero.slide2.subtitle'), brand: 'ADOBE' },
    { title: t('hero.slide3.title'), subtitle: t('hero.slide3.subtitle'), brand: 'KASPERSKY / 3CX / EKAHAU' },
    { title: t('hero.slide4.title'), subtitle: t('hero.slide4.subtitle'), brand: 'PROCUREMENT' },
  ];

  const quickNavItems = [
    { icon: Shield, title: t('quicknav.licenses'), desc: t('quicknav.licenses_desc'), color: '#0D47A1', link: '/licenses' },
    { icon: Headphones, title: t('quicknav.support'), desc: t('quicknav.support_desc'), color: '#D32F2F', link: '/technical-support' },
    { icon: Truck, title: t('quicknav.procurement'), desc: t('quicknav.procurement_desc'), color: '#1A237E', link: '/procurement' },
    { icon: Code, title: t('quicknav.hosting'), desc: t('quicknav.hosting_desc'), color: '#B71C1C', link: '/apps-hosting' },
  ];

  const industries = [
    { icon: Heart, key: 'health', color: '#E53935' },
    { icon: Briefcase, key: 'consulting', color: '#0D47A1' },
    { icon: Building2, key: 'banks', color: '#1A237E' },
    { icon: Factory, key: 'manufacturing', color: '#D32F2F' },
    { icon: GraduationCap, key: 'education', color: '#0D47A1' },
    { icon: Droplets, key: 'petroleum', color: '#B71C1C' },
  ];

  const services = [
    { icon: Shield, title: t('services.licenses.title'), desc: t('services.licenses.desc'), color: '#0D47A1', products: ['Microsoft', 'Adobe', '3CX', 'Kaspersky', 'Ekahau'], link: '/licenses' },
    { icon: Headphones, title: t('services.support.title'), desc: t('services.support.desc'), color: '#D32F2F', products: ['Microsoft', 'Adobe', '3CX', 'Kaspersky', 'Ekahau'], link: '/technical-support' },
    { icon: Truck, title: t('services.procurement.title'), desc: t('services.procurement.desc'), color: '#1A237E', link: '/procurement' },
    { icon: Server, title: t('services.hosting.title'), desc: t('services.hosting.desc'), color: '#B71C1C', link: '/apps-hosting' },
  ];

  const inputStyle: React.CSSProperties = { width: '100%', padding: '14px 16px', border: '1px solid var(--border)', fontSize: 15, outline: 'none', boxSizing: 'border-box' as const, background: 'white', fontFamily: 'var(--font-body)' };

  return (
    <main style={{ overflowX: 'hidden' }}>
      {/* ═══ HERO SLIDER ═══ */}
      <section data-testid="hero-section" style={{ position: 'relative', height: '100vh', minHeight: 600, maxHeight: 900, overflow: 'hidden', paddingTop: 72 }}>
        {SLIDE_IMAGES.map((img, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            opacity: currentSlide === i ? 1 : 0,
            transform: currentSlide === i ? 'scale(1)' : 'scale(1.08)',
            transition: 'opacity 0.8s ease, transform 6s ease',
            zIndex: currentSlide === i ? 1 : 0,
          }}>
            <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0.5) 50%, rgba(10,17,40,0.3) 100%)' }} />
          </div>
        ))}

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto', padding: '0 24px', height: '100%', display: 'flex', alignItems: 'center' }}>
          <div style={{ maxWidth: 640 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary-light, #EF5350)', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 16, opacity: 0.9 }}>
              {slideContent[currentSlide]?.brand}
            </div>
            <h1 key={`title-${currentSlide}`} style={{
              fontFamily: 'var(--font-heading)', fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 700, lineHeight: 1.05,
              letterSpacing: '-0.03em', color: 'white', marginBottom: 20,
              animation: 'slideInLeft 0.6s ease-out',
            }}>
              {slideContent[currentSlide]?.title}
            </h1>
            <p key={`sub-${currentSlide}`} style={{
              fontSize: 'clamp(15px, 1.8vw, 18px)', lineHeight: 1.7, color: 'rgba(255,255,255,0.8)', marginBottom: 36, maxWidth: 500,
              animation: 'fadeInUp 0.6s ease-out 0.15s both',
            }}>
              {slideContent[currentSlide]?.subtitle}
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Link to="/licenses" data-testid="hero-cta-button" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'var(--secondary)', color: 'white', padding: '16px 32px',
                fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', transition: 'background 0.2s',
              }}>
                {t('hero.cta')} <ArrowRight size={18} />
              </Link>
              <a href="#services" data-testid="hero-explore-button" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                border: '2px solid rgba(255,255,255,0.4)', color: 'white', padding: '14px 32px',
                fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', transition: 'all 0.2s',
              }}>
                {t('hero.explore')}
              </a>
            </div>
          </div>
        </div>

        {/* Slider controls */}
        <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', alignItems: 'center', gap: 20 }}>
          <button onClick={prevSlide} data-testid="slider-prev" style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'background 0.2s' }}>
            <ChevronLeft size={20} />
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {SLIDE_IMAGES.map((_, i) => (
              <button key={i} data-testid={`slider-dot-${i}`} onClick={() => setCurrentSlide(i)} style={{
                width: currentSlide === i ? 32 : 10, height: 10, border: 'none', cursor: 'pointer',
                background: currentSlide === i ? 'var(--secondary)' : 'rgba(255,255,255,0.4)',
                transition: 'all 0.3s',
              }} />
            ))}
          </div>
          <button onClick={nextSlide} data-testid="slider-next" style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'background 0.2s' }}>
            <ChevronRight size={20} />
          </button>
        </div>
      </section>

      {/* ═══ QUICK NAV — What are you looking for? ═══ */}
      <section data-testid="quicknav-section" style={{ padding: '0 24px', position: 'relative', zIndex: 10 }}>
        <div style={{ maxWidth: 1280, margin: '-60px auto 0', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }} className="quicknav-grid">
          {quickNavItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <Link to={item.link} key={i} data-testid={`quicknav-item-${i}`} style={{
                background: i % 2 === 0 ? '#0A1128' : '#0D47A1', padding: '36px 28px',
                display: 'flex', flexDirection: 'column', gap: 12, transition: 'transform 0.2s, background 0.3s',
                textDecoration: 'none', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#0A1128' : '#0D47A1'; }}
              >
                <Icon size={28} style={{ color: 'white', opacity: 0.9 }} strokeWidth={1.5} />
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>{item.title}</h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{item.desc}</p>
                <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.5)', marginTop: 'auto' }} />
              </Link>
            );
          })}
        </div>
      </section>

      {/* ═══ PARTNERS MARQUEE ═══ */}
      <section id="partners" data-testid="partners-section" style={{ background: 'var(--bg-section)', padding: '48px 0', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', marginBottom: 24 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.2em', textTransform: 'uppercase', textAlign: 'center' }}>{t('partners.subtitle')}</p>
        </div>
        <div style={{ overflow: 'hidden', position: 'relative' }}>
          <div style={{ display: 'flex', animation: 'marquee 20s linear infinite', width: 'fit-content' }}>
            {[...partners, ...partners, ...partners, ...partners].map((p, i) => (
              <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'clamp(18px, 2.5vw, 28px)', fontWeight: 600, color: 'var(--text-muted)', opacity: 0.4, padding: '0 40px', whiteSpace: 'nowrap', transition: 'opacity 0.3s' }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0.4')}
              >{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section id="services" data-testid="services-section" style={{ padding: '96px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ marginBottom: 56, maxWidth: 600 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>{t('services.subtitle')}</p>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>{t('services.title')}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} data-testid={`service-card-${i}`} style={{
                  background: 'white', border: '1px solid var(--border)', padding: 32, transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s', cursor: 'pointer',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = s.color; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <div style={{ width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}12`, marginBottom: 20 }}>
                    <Icon size={24} style={{ color: s.color }} strokeWidth={1.5} />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{s.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary)', marginBottom: 16 }}>{s.desc}</p>
                  {s.products && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {s.products.map(p => (
                        <span key={p} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '3px 8px', background: 'var(--bg-section)', border: '1px solid var(--border)', color: 'var(--text-secondary)', letterSpacing: '0.05em' }}>{p}</span>
                      ))}
                    </div>
                  )}
                  <Link to={s.link || '/quote'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16, fontSize: 13, fontWeight: 600, color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('nav.quote')} <ChevronRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES ═══ */}
      <section id="industries" data-testid="industries-section" style={{ padding: '96px 24px', background: '#0A1128' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ marginBottom: 56, textAlign: 'center' }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary-light, #EF5350)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>{t('industries.subtitle')}</p>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, letterSpacing: '-0.03em', color: 'white' }}>{t('industries.title')}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }} className="industries-grid">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div key={i} data-testid={`industry-card-${i}`} style={{
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', padding: 32,
                  transition: 'all 0.3s', cursor: 'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = ind.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Icon size={28} style={{ color: ind.color, marginBottom: 16, opacity: 0.9 }} strokeWidth={1.5} />
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 600, color: 'white', marginBottom: 8 }}>{t(`industries.${ind.key}`)}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.55)' }}>{t(`industries.${ind.key}_desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ ABOUT US ═══ */}
      <section id="about" data-testid="about-section" style={{ padding: '96px 24px', background: 'var(--bg-section)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="about-grid">
            <div>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>{t('about.subtitle')}</p>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)', marginBottom: 24 }}>{t('about.title')}</h2>
              <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: 16 }}>{t('about.intro')}</p>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: 32 }}>{t('about.org')}</p>

              <div style={{ display: 'flex', gap: 40, marginBottom: 32 }}>
                <div>
                  <p style={{ fontFamily: 'var(--font-heading)', fontSize: 40, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>8+</p>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>{t('about.experience')}</p>
                </div>
                <div>
                  <p style={{ fontFamily: 'var(--font-heading)', fontSize: 40, fontWeight: 700, color: 'var(--secondary)', lineHeight: 1 }}>18+</p>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>{t('about.clients')}</p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {[
                { icon: Award, title: t('about.professionalism'), desc: t('about.professionalism_desc'), color: '#0D47A1' },
                { icon: Lightbulb, title: t('about.creativity'), desc: t('about.creativity_desc'), color: '#D32F2F' },
                { icon: Users, title: t('about.customer_value'), desc: t('about.customer_value_desc'), color: '#1A237E' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} data-testid={`about-pillar-${i}`} style={{
                    background: 'white', border: '1px solid var(--border)', padding: 28,
                    display: 'flex', gap: 20, transition: 'border-color 0.2s, transform 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = item.color; e.currentTarget.style.transform = 'translateX(4px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateX(0)'; }}
                  >
                    <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${item.color}10`, flexShrink: 0 }}>
                      <Icon size={22} style={{ color: item.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>{item.title}</h3>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA PARTNERSHIP ═══ */}
      <section data-testid="cta-section" style={{ padding: '80px 24px', background: 'var(--primary)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, color: 'white', marginBottom: 8 }}>{t('cta.partnership')}</h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.75)', maxWidth: 500 }}>{t('cta.partnership_desc')}</p>
          </div>
          <a href="#contact-form" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'white', color: 'var(--primary)', padding: '16px 36px',
            fontWeight: 700, fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.08em', transition: 'transform 0.2s',
          }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {t('cta.contact_us')} <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* ═══ CONTACT FORM ═══ */}
      <section id="contact-form" data-testid="contact-section" style={{ padding: '96px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64 }} className="contact-grid">
            <div>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>{t('contact.subtitle')}</p>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)', marginBottom: 32 }}>{t('contact.title')}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,71,161,0.08)', flexShrink: 0 }}>
                    <MapPin size={20} style={{ color: 'var(--primary)' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>{t('contact.address')}</p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6 }}>Rua 3, Bloco 10, Predio 12-A, Apt. 302<br />Centralidade do Sequele (Cacuaco), Angola</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,71,161,0.08)', flexShrink: 0 }}>
                    <Mail size={20} style={{ color: 'var(--primary)' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>Email</p>
                    <a href="mailto:sales@maluzts.com" style={{ color: 'var(--primary)', fontSize: 15 }}>sales@maluzts.com</a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,71,161,0.08)', flexShrink: 0 }}>
                    <Phone size={20} style={{ color: 'var(--primary)' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>{t('contact.phone')}</p>
                    <a href="tel:+244226438350" style={{ color: 'var(--primary)', fontSize: 15 }}>+244 226 438 350</a>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleContact} data-testid="contact-form-element">
              {contactSuccess && (
                <div data-testid="contact-success" style={{ padding: '12px 16px', background: 'rgba(13,71,161,0.08)', border: '1px solid var(--primary)', color: 'var(--primary)', marginBottom: 20, fontSize: 14, fontWeight: 500 }}>{t('contact.success')}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <input data-testid="contact-name-input" placeholder={t('contact.name')} value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} required style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = 'var(--primary)')} onBlur={e => (e.currentTarget.style.borderColor = 'var(--border)')} />
                <input data-testid="contact-email-input" type="email" placeholder={t('contact.email')} value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} required style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = 'var(--primary)')} onBlur={e => (e.currentTarget.style.borderColor = 'var(--border)')} />
                <input data-testid="contact-subject-input" placeholder={t('contact.subject')} value={contactForm.subject} onChange={e => setContactForm({...contactForm, subject: e.target.value})} style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = 'var(--primary)')} onBlur={e => (e.currentTarget.style.borderColor = 'var(--border)')} />
                <textarea data-testid="contact-message-input" placeholder={t('contact.message')} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} required rows={5} style={{...inputStyle, resize: 'vertical' as const}}
                  onFocus={e => (e.currentTarget.style.borderColor = 'var(--primary)')} onBlur={e => (e.currentTarget.style.borderColor = 'var(--border)')} />
                <button type="submit" data-testid="contact-submit-btn" disabled={contactLoading}
                  style={{ padding: '16px 32px', background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', transition: 'background 0.2s', opacity: contactLoading ? 0.7 : 1, cursor: 'pointer' }}>
                  {contactLoading ? '...' : t('contact.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* ═══ RESPONSIVE STYLES ═══ */}
      <style>{`
        @media (max-width: 900px) {
          .quicknav-grid { grid-template-columns: repeat(2, 1fr) !important; margin-top: -40px !important; }
          .about-grid { grid-template-columns: 1fr !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .industries-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .quicknav-grid { grid-template-columns: 1fr !important; margin-top: -20px !important; }
          .industries-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </main>
  );
}
